import { useState } from "react";
import { ApiUserMinimal } from "src/api/types";
import Skeleton from "src/components/Skeleton";
import TitleCut, { TitleCutBackground } from "src/components/TitleCut";
import useTeams from "src/hooks/api/services/users/useTeams";
import useAuth from "src/hooks/selectors/useAuth";
import useVariant, { VariantTypes } from "src/hooks/useVariant";

const sizes = {
	xsmall: "rounded-md w-8 h-8",
	small: "rounded-lg w-10 h-10",
	regular: "rounded-lg w-12 h-12",
	medium: "rounded-lg w-16 h-16",
	large: "rounded-lg w-24 h-24",
	button: "rounded-md w-[40px] h-[40px]",
	xlarge: "rounded-lg w-32 h-32 text-[32px]",
};

export type AvatarSize = keyof typeof sizes;

export interface AvatarProps {
	src?: string;
	variant: VariantTypes;
	className?: string;
	alt?: string;
	size: AvatarSize;
	defaultIcon?: "smiley";
}

const Avatar = ({
	src,
	alt,
	className: _className,
	size,
	defaultIcon,
	variant,
}: AvatarProps) => {
	const _variant = useVariant(variant, {
		noBorder: true,
	});
	const [isError, setIsError] = useState(src ? false : true);

	let className = `overflow-hidden flex justify-center items-center ${
		isError ? _variant : ""
	} ${_className || ""} ${sizes[size]}`;

	return (
		<div {...{ className }}>
			{src && !isError ? (
				<img
					onError={() => setIsError(true)}
					className="w-full h-full object-cover"
					src={src}
					alt={alt}
				/>
			) : defaultIcon === "smiley" ? (
				<img
					className="w-full h-full block p-2"
					src="/assets/images/emoji/smiley.png"
					alt=""
				/>
			) : (
				<i className="fas fa-user"></i>
			)}
		</div>
	);
};

Avatar.defaultProps = {
	size: "small",
	variant: "dark",
};

interface LoaderProps {
	size: AvatarSize;
}

const Loader = ({ size }: LoaderProps) => {
	return <Skeleton className={sizes[size]} />;
};

Loader.defaultProps = {
	size: "small",
};

Avatar.Loader = Loader;

const USER_VARIANTS = {
	bold: {
		label: "font-bold",
	},
	light: {
		label: "",
	},
};

interface UserProps {
	user: ApiUserMinimal;
	subtitle?: any;
	className?: string;
	avatar?: {
		size?: AvatarSize;
		className?: string;
	};
	variant: keyof typeof USER_VARIANTS;
	options?: {
		iconOnly?: boolean;
		titleCutBackground?: TitleCutBackground;
	};
}

const User = ({
	user,
	subtitle,
	className,
	avatar,
	variant: _variant,
	options,
}: UserProps) => {
	const auth = useAuth();
	const variant = USER_VARIANTS[_variant];
	const { teams } = useTeams();

	if (!subtitle && user?.company) {
		subtitle = `${user.company?.name}`;
		if (user.teams) {
			const names = teams
				.filter((team) => (user.teams || []).includes(team.slug))
				.map((team) => team.name);
			const formatter = new Intl.ListFormat("nl", {
				style: "long",
				type: "conjunction",
			});
			if (names) {
				subtitle = formatter.format(names);
			}
		}
	}

	return (
		<div className={`flex items-center gap-2 ${className}`}>
			<Avatar
				size={avatar?.size || "small"}
				className={avatar?.className}
				src={user?.avatar}
			/>
			{!options?.iconOnly && (
				<div
					className={`flex flex-col whitespace-nowrap max-w-[110px]`}
				>
					<p className={variant.label}>
						{user.company && auth.type === "student"
							? `${user.first_name}`
							: user.full_name}
					</p>
					{subtitle && typeof subtitle === "string" ? (
						<TitleCut
							color={options?.titleCutBackground}
							className="h-[19px]"
							as="small"
						>
							{subtitle}
						</TitleCut>
					) : (
						subtitle
					)}
				</div>
			)}
		</div>
	);
};

User.defaultProps = {
	variant: "bold",
};

Avatar.User = User;

export default Avatar;
